import type { HistorySearchItem } from "./types/suggestion";
import type { TrackingData } from "./types/tracking";
import { DATA, SELECTOR, SquirrelURL } from "./helper";
import { FALLBACK_SEARCH_URL, HISTORY_MODE, TRACK_LABEL } from "./types/constants";
import {
  arrowTemplate,
  checkboxIconTemplate,
  checkboxInputTemplate,
  footerLiTemplate,
  headlineTemplate,
  itemLabelTemplate,
  keywordsTemplate,
} from "./templates/history.templates";
/*🤫*/
/*🤫*/
import templateString from "../public/templates/historyLayer.mustache";

const Mustache = window.Mustache;

export class SearchHistory {
  private suggestLayer: HTMLDivElement;
  private searchField: HTMLInputElement;
  private searchForm: HTMLFormElement;
  private mode: HISTORY_MODE;

  constructor() {
    this.suggestLayer = document.querySelector(SELECTOR.LAYER) as HTMLDivElement;
    this.searchField = document.querySelector(SELECTOR.SEARCH_FIELD) as HTMLInputElement;
    this.searchForm = document.querySelector(SELECTOR.SEARCH_FORM) as HTMLFormElement;
    this.mode = HISTORY_MODE.NORMAL;
  }

  init() {
    this.suggestLayer = document.querySelector(SELECTOR.LAYER) as HTMLDivElement;
    this.searchField = document.querySelector(SELECTOR.SEARCH_FIELD) as HTMLInputElement;
    this.searchForm = document.querySelector(SELECTOR.SEARCH_FORM) as HTMLFormElement;
    this.mode = HISTORY_MODE.NORMAL;
  }

  show() {
    const response: HistorySearchItem[] = window.o_squirrel.submitHistory.retrieveViewedSearch().slice().reverse();
    const renderedTemplate: string = this.renderHistory(response);
    this.updateSuggestLayer(renderedTemplate);
    window.o_squirrel.searchSuggest.showLayer();
  }

  private updateSuggestLayer(template: string) {
    const historyLayer = document.querySelector(".js_squirrel_history_layer") as HTMLDivElement;
    window.o_util.core.bind(historyLayer, window.o_squirrel.searchSuggest.updateSuggestionLayer)(template);
  }

  clear() {
    this.mode = HISTORY_MODE.NORMAL;
    this.removeChecked();
  }

  removeChecked() {
    Array.from(this.suggestLayer.querySelectorAll<HTMLSpanElement>(".js_squirrel_searchHistory__term")).forEach(
      (elem: HTMLSpanElement) => {
        elem.removeAttribute("checked");
      }
    );
  }

  clickEditAction() {
    this.toggleMode();
    if (this.mode == HISTORY_MODE.SELECT) {
      this.removeChecked();
    }
    this.show();
  }

  clickTermAction(evt: Event) {
    if (this.mode === HISTORY_MODE.SELECT) {
      this.selectItemAction(evt);
    } else {
      this.submit(evt);
    }
  }

  clickDeleteAllAction(evt: Event) {
    if (this.mode === HISTORY_MODE.SELECT) {
      this.deleteSelected(evt);
    } else {
      this.deleteAll();
    }
  }

  private toggleMode() {
    this.mode = this.mode === HISTORY_MODE.SELECT ? HISTORY_MODE.NORMAL : HISTORY_MODE.SELECT;
  }

  private renderHeadline(selectMode: boolean): string {
    return Mustache.render(headlineTemplate, {
      selectMode,
      labelEdit: "Bearbeiten",
      labelAbort: "Abbrechen",
    });
  }

  private renderKeyword(selectMode: boolean, searches: any[]): string {
    const checkboxItem = selectMode ? checkboxInputTemplate : checkboxIconTemplate;
    return Mustache.render(
      keywordsTemplate,
      { selectMode, searches },
      {
        checkboxItem,
        itemLabelTemplate,
        arrowTemplate,
      }
    );
  }

  private renderFooter(itemSelected: boolean): string {
    const label: string = itemSelected ? "Auswahl löschen" : "Alle löschen";
    return Mustache.render(footerLiTemplate, { label });
  }

  renderHistory(response: HistorySearchItem[]): string {
    const searches = this.getSearches(response);
    const selectMode = this.mode === HISTORY_MODE.SELECT;
    const itemSelected = searches.some((search) => search.checked);

    return Mustache.render(
      templateString,
      {},
      {
        headlineTemplate: this.renderHeadline(selectMode),
        keywordTemplate: this.renderKeyword(selectMode, searches),
        footerLiTemplate: this.renderFooter(itemSelected && this.mode == HISTORY_MODE.SELECT),
      }
    );
  }

  private deleteSelected(evt: Event) {
    const items: number[] = Array.from(this.suggestLayer.querySelectorAll(SELECTOR.HISTORY_ITEM_SELECT))
      .filter(({ checked }: Partial<any>) => !!checked)
      .map((e: any) =>
        window.o_squirrel.submitHistory.getSearchPosition(
          window.o_squirrel.util.string.trim(e.parentElement.querySelector(SELECTOR.HISTORY_TERM).textContent)
        )
      );

    if (items.length <= 0) {
      this.deleteAll();
    } else {
      window.o_squirrel.submitHistory.deleteSearches(items);
      this.show();
      if (window.o_squirrel.submitHistory.isEmpty()) {
        this.updateSuggestLayer("");
        this.searchField.focus();
      }
    }
  }

  private deleteAll() {
    window.o_squirrel.submitHistory.deleteAllSearches();
    this.updateSuggestLayer("");
    this.searchField.focus();
  }

  private selectItemAction(evt: Event) {
    const elem: HTMLInputElement = evt.target as HTMLInputElement;
    if (elem.type === "checkbox") {
      const target: ParentNode = elem.offsetParent as ParentNode;
      const termElement: HTMLSpanElement = target.querySelector(SELECTOR.HISTORY_TERM) as HTMLSpanElement;

      termElement.setAttribute("checked", elem.checked + "");
      this.show();
    } else {
      this.submit(evt);
    }
  }

  private getSearches(historyItems: HistorySearchItem[]): { term: string; checked: boolean | null }[] {
    const items: HTMLSpanElement[] = Array.from(this.suggestLayer.querySelectorAll(".js_squirrel_searchHistory__term"));
    return historyItems.map((item: HistorySearchItem, index: number) => {
      const isChecked = (): boolean | null => {
        const elem = items.filter((e: HTMLSpanElement): boolean => {
          return e.getAttribute("data-term") == item.term;
        });
        return !!elem && !!elem[0] && elem[0].getAttribute("checked") === "true";
      };
      return { term: item.term, checked: isChecked() };
    });
  }

  private submit(evt: Event) {
    const elem: HTMLInputElement = evt.target as HTMLInputElement;
    const target: HTMLLIElement = elem.closest("li") as HTMLLIElement;
    const termElement: HTMLDivElement = target.querySelector(SELECTOR.HISTORY_TERM) as HTMLDivElement;
    const ul: HTMLUListElement = elem.closest("ul") as HTMLUListElement;
    const position: number = Array.from(ul.children).indexOf(target) + 1;

    this.searchField.value = termElement.getAttribute(DATA.TERM) as string;
    const action = FALLBACK_SEARCH_URL(termElement.getAttribute(DATA.TERM) as string, {});
    const urlObject = new SquirrelURL(action);
    const hashTrackingParameters = urlObject.getTrackingParameter();
    const trackingData: TrackingData = {
      [TRACK_LABEL.SUGGEST_POS]: position
    };
    window.o_squirrel.suggestTracking.trackUsage(trackingData, hashTrackingParameters, false, false, false, true);
    window.o_squirrel.submitHistory.addSearch(this.searchField.value);
    window.o_squirrel.util.global.setLocation(urlObject.getUrlWithoutTrackingParameter());
  }
}

const instance = new SearchHistory();
export const searchHistory = {
  init: instance.init.bind(instance),
  show: instance.show.bind(instance),
  clear: instance.clear.bind(instance),
  renderHistory: instance.renderHistory.bind(instance),
  clickEditAction: instance.clickEditAction.bind(instance),
  clickTermAction: instance.clickTermAction.bind(instance),
  clickDeleteAllAction: instance.clickDeleteAllAction.bind(instance),
};
